import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { getAnchorName, buildSubtypeSubsegmentLink } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { LazyContentCollapseTransitioned } from 'site-modules/shared/components/lazy-content-collapse/lazy-content-collapse-transitioned';
import { RankingsVehicleCard } from 'site-modules/upper-funnel/components/type/rankings-vehicle-card/rankings-vehicle-card';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { RANKING_DATA } from 'site-modules/upper-funnel/constants/ranking-data';
import { VEHICLES_NUM_TO_RANK } from 'site-modules/shared/constants/upper-funnel/type-rankings';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { getSubtypeAds } from 'site-modules/upper-funnel/pages/subtype/get-subtype-ads';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { CAR_TYPES } from 'site-modules/shared/constants/upper-funnel/car-types';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
// eslint-disable-next-line venom/no-internal-modules
import { EDSButton } from 'site-modules/edmunds-design-system/components/eds-button/eds-button';

import { ReadMore } from 'site-modules/upper-funnel/components/type/read-more/read-more';
import { SUV_EXTRA_HEADING_DATA } from 'site-modules/upper-funnel/components/type/vehicle-subtype-rankings-list/ads-11602-suv-read-more-data';

import './vehicle-subtype-rankings-list.scss';

export const getSubtypeAdPos = (index, isMobile, trkParam) => {
  switch (index) {
    case 3:
      return getSubtypeAds(trkParam, isMobile, true).pos1;
    case 6:
      return getSubtypeAds(trkParam, isMobile, true).pos2;
    case 9:
      return getSubtypeAds(trkParam, isMobile, true).segmentListing;
    case 12:
      return getSubtypeAds(trkParam, isMobile, true).videoAd;
    case 15:
      return getSubtypeAds(trkParam, isMobile, true).filmstrip;
    case 18:
      return getSubtypeAds(trkParam, isMobile, true).pos3;
    case 21:
      return getSubtypeAds(trkParam, isMobile, true).pos4;
    default:
      return null;
  }
};

export const getAdUnit = (idx, trkParam) => {
  const mrectPos = idx < 2 ? idx + 1 : idx;
  switch (idx) {
    case 2: // video ad
      return (
        <Experiment name="ads-11436" showDefault>
          <Recipe name="ctrl" isDefault>
            <NativeVideoAd wrapperClass="mb-2 mb-md-0" useMinHeight renderWhenViewable verticalOffset="100%" />;
          </Recipe>
          <Recipe name="chal" />
        </Experiment>
      );
    case 0: // mrect pos 1
    case 1: // mrect pos 2
    case 3: // mrect pos 3
    case 4: // mrect pos 4
      return (
        <MrectNative
          position={`${mrectPos}`}
          refreshable={false}
          customTargeting={trkParam}
          useMinHeight
          // renderWhenViewable={isMobile}
          renderWhenViewable
          verticalOffset="100%"
        />
      );
    default:
      return null;
  }
};

const getRatedVehicleCardsList = ({
  segmentRatings,
  startPos,
  endPos,
  subtypeId,
  isEvData,
  isRatedList,
  index,
  renderSeeAllBtn,
  onSelectCar,
  selectedCars,
  isCollapseItem,
  isSubtypePage,
  subtypeAdPlacementIndices,
  isMobile,
  trkParam,
  skipRenderAds,
  isReplaceImage,
  type,
  isSubtypeExpansionPage,
  isSingleCategory = false,
  dataTrackingParent,
}) => {
  const showRank = segmentRatings.length >= VEHICLES_NUM_TO_RANK;
  const slicedRatings = segmentRatings.slice(startPos, endPos);
  const indexToUse = isCollapseItem ? 0 : 1;
  const largeCards = [];
  const isVideo = index === 2;

  const defaultAdUnitWrapper = (
    <div
      className={classnames('d-inline-block ms-auto mx-auto mt-3_5', {
        'mt-lg-1 me-xl-2_5 me-lg-0': !isVideo,
        'mt-lg-0 me-lg-0 me-xl-0_25': isVideo,
      })}
      style={
        isVideo
          ? { maxWidth: '360px', minWidth: '300px', width: '33%' } // video ad
          : { width: '300px', height: '268px' } // mrect ad
      }
    >
      {getAdUnit(index, trkParam)}
    </div>
  );

  const smallCards = slicedRatings.reduce((acc, segmentRating, i) => {
    const { rank, baseStyleId } = segmentRating;
    const subtypeAbsoluteIndex = subtypeAdPlacementIndices && subtypeAdPlacementIndices[index].startIndex + i + 1;
    if (rank === 1 || isSubtypePage) {
      largeCards.push(
        <div key={`${baseStyleId}-${subtypeId}`} className="mb-1">
          <RankingsVehicleCard
            showRank={showRank}
            showEvData={isEvData}
            vehicle={segmentRating}
            onSelectCar={onSelectCar}
            selected={!!selectedCars.find(({ id: carId }) => carId === baseStyleId)}
            selectedCarsNumber={selectedCars.length}
            subtypeId={parseInt(subtypeId, 10)}
            isLargeCard
            isRatedList={isRatedList}
            isSubtypePage={isSubtypePage}
            isReplaceImage={isReplaceImage && index === 0}
            type={type}
            isMobile={isMobile}
            isSubtypeExpansionPage={isSubtypeExpansionPage}
            dataTrackingParent={dataTrackingParent}
          />
          {getSubtypeAdPos(subtypeAbsoluteIndex, isMobile, trkParam)}
        </div>
      );
    } else {
      acc.push(
        <Col
          xs={12}
          sm={6}
          lg={4}
          key={`${baseStyleId}-${subtypeId}`}
          className={classnames('d-md-inline-block align-top ps-md-0 mx-auto mx-sm-0 mb-1', {
            'pe-md-1': i === indexToUse || isSingleCategory,
            'pe-lg-0': isSingleCategory && (i + indexToUse) % 2 === 0 && i > 0 && indexToUse !== 1,
            'pe-md-0': i > indexToUse && !isSingleCategory,
          })}
          style={{ maxWidth: '542px' }}
        >
          <RankingsVehicleCard
            showRank={showRank}
            showEvData={isEvData}
            vehicle={segmentRating}
            onSelectCar={onSelectCar}
            selected={!!selectedCars.find(({ id: carId }) => carId === baseStyleId)}
            selectedCarsNumber={selectedCars.length}
            subtypeId={parseInt(subtypeId, 10)}
            isRatedList={isRatedList}
            isReplaceImage={isReplaceImage && index === 0}
            type={type}
            dataTrackingParent={dataTrackingParent}
          />
        </Col>
      );
    }
    return acc;
  }, []);

  return (
    <Fragment>
      {largeCards.map(card => card)}
      {isSubtypePage === false && (
        <Row
          className={classnames('small-cards d-sm-flex mx-md-0', {
            'mt-2 mt-lg-0': !renderSeeAllBtn && skipRenderAds,
          })}
        >
          {smallCards.map(card => card)}
          {renderSeeAllBtn && renderSeeAllBtn('hidden-lg-up w-100')}
          {!skipRenderAds && index < 5 && defaultAdUnitWrapper}
        </Row>
      )}
      {renderSeeAllBtn && renderSeeAllBtn('hidden-lg-down')}
    </Fragment>
  );
};

export function VehicleSubtypeRankingsList({
  trkParam,
  onSelectCar,
  selectedCars,
  selectedCarsNumber,
  subtype,
  isAlwaysOpened,
  isMobile,
  isHighlights,
  isSubtypePage,
  isSubtypeExpansionPage,
  dataTrackingParent,
  index,
  isSingleCategory,
  subtypeAdPlacementIndices,
  isReplaceImage,
  type,
}) {
  const { description, segmentRatings, id, displayName, edmundsTypeCategory } = subtype;
  const withRating = isSubtypePage ? segmentRatings : segmentRatings.slice(0, RANKING_DATA.maxRatedVehiclesNum);
  const hasCollapsedList = withRating.length > RANKING_DATA.DISPLAYED_VEHICLES_NUM && !isAlwaysOpened;
  const isRatedList = !withRating.length || withRating.length >= VEHICLES_NUM_TO_RANK;
  const listId = `${getAnchorName(displayName)}-${id}`;
  const headingId = `${listId}-heading`;

  const vehicleCardListProps = {
    segmentRatings: withRating,
    subtypeId: id,
    isEvData: edmundsTypeCategory.toLowerCase() === 'electric',
    isRatedList,
    index,
    onSelectCar,
    selectedCars,
    selectedCarsNumber,
    isMobile,
    dataTrackingParent,
  };

  function renderSeeAllBtn(classes) {
    const subtypeSubsegmentLink = buildSubtypeSubsegmentLink(edmundsTypeCategory, displayName);
    const displayNameToUse = displayName.toLowerCase().replace('suv', 'SUV');
    return (
      <Fragment>
        {hasCollapsedList && !subtypeSubsegmentLink && (
          <LazyContentCollapseTransitioned
            btnTrackingId="subtype_list"
            textOpen="See fewer"
            textClosed={isMobile ? 'See all' : `See all ${displayNameToUse}`}
            className={classnames('col-12', classes)}
          >
            {getRatedVehicleCardsList({
              ...vehicleCardListProps,
              startPos: RANKING_DATA.DISPLAYED_VEHICLES_NUM,
              isHighlights,
              isCollapseItem: true,
              isSubtypePage,
              skipRenderAds: true,
              type,
              isMobile,
            })}
          </LazyContentCollapseTransitioned>
        )}
        {!isSubtypePage && subtypeSubsegmentLink && (
          <div className={classnames('col-12 pt-0_25 pt-md-0_75 text-center', classes)}>
            <EDSButton href={subtypeSubsegmentLink} aria-labelledby={headingId} size="large">
              <span className="fw-medium">
                See all <span className="visually-hidden">{displayNameToUse}</span>
              </span>
            </EDSButton>
          </div>
        )}
      </Fragment>
    );
  }

  const renderDescription = isDefault => (
    <p
      className={classnames('size-16 text-cool-gray-30', {
        'mb-2': isDefault,
        'mb-0': !isDefault,
      })}
      style={{ maxWidth: '940px' }}
    >
      {description}
    </p>
  );
  const descriptionCopy = SUV_EXTRA_HEADING_DATA[displayName.trim().replace(/\s/g, '_').toLowerCase()];

  return (
    <ScrollElement
      id={`${listId}`}
      aria-describedby={headingId}
      className="vehicle-subtype-rankings-list list-unstyled pos-r mb-3_5"
      data-tracking-parent={dataTrackingParent}
    >
      {!isSubtypeExpansionPage && (
        <Row className="subtype-heading justify-content-between">
          <Col xs={12} md={'auto'} className="size-24 pt-1 pt-md-0 mb-0_5">
            <h2
              id={headingId}
              className="heading-2 d-inline-block mb-0_75 text-cool-gray-20 text-capitalize"
              data-test="subtype-heading"
            >
              {`Best ${displayName}`}
            </h2>
            {type !== CAR_TYPES.SUV && renderDescription(true)}
            {type === CAR_TYPES.SUV && (
              <FeatureFlag name="enableTypeReadMore">
                {enabled =>
                  enabled ? (
                    <ReadMore
                      containerClasses="size-16 text-cool-gray-30 mb-2"
                      summaryClasses="pt-0_5 text-blue-50"
                      baseDescriptionCopy={renderDescription(false)}
                      style={{ maxWidth: '940px' }}
                    >
                      <Fragment>
                        {descriptionCopy && <div className="pt-1">{descriptionCopy}</div>}
                        <ul className="pt-1 list-unstyled w-100">
                          {segmentRatings.slice(0, 5).map(({ modelYear }, i) => {
                            const { modelName, makeName, year } = modelYear;
                            return (
                              <li key={`${modelName}-${makeName}`}>
                                <span className="fw-bold pe-0_5">
                                  <small>#</small>
                                  {i + 1}
                                </span>
                                {year} {makeName} {modelName}
                              </li>
                            );
                          })}
                        </ul>
                      </Fragment>
                    </ReadMore>
                  ) : (
                    renderDescription(true)
                  )
                }
              </FeatureFlag>
            )}
          </Col>
        </Row>
      )}
      {!isRatedList && (
        <div className="px-1 py-0_5 mb-1 text-gray-darker medium rounded">
          <i className="not-enough-icon icon-info size-16 me-0_5" aria-hidden />
          Not enough vehicles yet to rank
        </div>
      )}
      {getRatedVehicleCardsList({
        ...vehicleCardListProps,
        startPos: 0,
        // rendering all cards if no collapsed list
        endPos: hasCollapsedList ? RANKING_DATA.DISPLAYED_VEHICLES_NUM : undefined,
        renderSeeAllBtn: isSingleCategory ? undefined : renderSeeAllBtn,
        isSubtypePage,
        subtypeAdPlacementIndices,
        trkParam,
        isReplaceImage,
        type,
        isSubtypeExpansionPage,
        isSingleCategory,
      })}
      {
        /** EDGE CASE:
         * Only for parent type page with only one category, such as the minivan page,
         * who does not have a subtype page:
         * Show all the remainder of the small cards on the parent page
         */
        isSingleCategory &&
          !isSubtypePage &&
          getRatedVehicleCardsList({
            ...vehicleCardListProps,
            startPos: RANKING_DATA.DISPLAYED_VEHICLES_NUM,
            isHighlights,
            isCollapseItem: true,
            isSubtypePage: false,
            skipRenderAds: true,
            type,
            isSingleCategory,
          })
      }
    </ScrollElement>
  );
}

VehicleSubtypeRankingsList.propTypes = {
  subtype: TypeRankingsEntities.VehicleRanking.isRequired,
  onSelectCar: PropTypes.func.isRequired,
  selectedCars: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedCarsNumber: PropTypes.number,
  isAlwaysOpened: PropTypes.bool,
  dataTrackingParent: PropTypes.string,
  isMobile: PropTypes.bool,
  isHighlights: PropTypes.bool,
  isSubtypePage: PropTypes.bool,
  isSubtypeExpansionPage: PropTypes.bool,
  index: PropTypes.number,
  trkParam: PropTypes.shape({
    trk: PropTypes.string,
  }),
  isSingleCategory: PropTypes.bool,
  subtypeAdPlacementIndices: PropTypes.arrayOf(PropTypes.shape({})),
  isReplaceImage: PropTypes.bool,
  type: PropTypes.string,
};

VehicleSubtypeRankingsList.defaultProps = {
  selectedCarsNumber: 0,
  isAlwaysOpened: true,
  dataTrackingParent: null,
  isMobile: false,
  isHighlights: false,
  isSubtypePage: false,
  isSubtypeExpansionPage: false,
  index: 0,
  trkParam: {},
  isSingleCategory: false,
  subtypeAdPlacementIndices: null,
  isReplaceImage: false,
  type: null,
};
